import { useApolloClient } from "@apollo/client";
import { CardContainer, LabeledValue } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import { Box, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as ibantools from "ibantools";
import * as Yup from "yup";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ManualSave } from "../../../commons/form-fields/ManualSave.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  OrganisationPaymentInfoBox_OrganisationFragment,
  OrganisationPaymentInfoBox_ViewerOrganisationFragment,
  useModifyOrganisationPaymentInformationMutation,
} from "./OrganisationPaymentInfoBox.generated.js";

type Organisation =
  | OrganisationPaymentInfoBox_OrganisationFragment
  | OrganisationPaymentInfoBox_ViewerOrganisationFragment;

interface Props {
  organisation: Organisation;
}

export function OrganisationPaymentInfoBox({ organisation }: Props) {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate("OrganisationProfile");
  const [isEditing, setIsEditing] = useState(false);

  if (viewer.organisation.id !== organisation.id)
    return <div>not own organisation</div>;

  return (
    <CardContainer
      title={t("Payment information")}
      ActionButton={
        !isEditing && (
          <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
            {viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
              <IconButton
                color="primary"
                size="small"
                onClick={() => setIsEditing(true)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            ) : null}
          </RestrictedByOrganisationPermissionWithDebug>
        )
      }
    >
      <Box
        sx={{
          p: 1,
        }}
      >
        {isEditing ? (
          <OrganisationPaymentInformationForm
            organisation={organisation}
            onDone={() => setIsEditing(false)}
          />
        ) : (
          <OrganisationPaymentInformationData organisation={organisation} />
        )}
      </Box>
    </CardContainer>
  );
}

function OrganisationPaymentInformationData({
  organisation,
}: {
  organisation: Organisation;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return (
    <Stack spacing={1} direction="column">
      <LabeledValue
        label={t("Bank name", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.bankName ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("IBAN", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.bankAccount ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("BIC code", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.bankCode ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
    </Stack>
  );
}

interface FormValues {
  bankName: string;
  bankAccount: string;
  bankCode: string;
}

function OrganisationPaymentInformationForm({
  organisation,
  onDone,
}: {
  organisation: Organisation;
  onDone: () => void;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [modifyOrganisation] = useModifyOrganisationPaymentInformationMutation({
    client,
  });

  const validationSchema = Yup.object().shape({
    bankName: Yup.string().label(t("Bank name")),
    bankAccount: Yup.string()
      .label(t("IBAN"))
      .test("iban", t("Invalid IBAN"), value => {
        const iban = ibantools.electronicFormatIBAN(value);
        return !iban || ibantools.isValidIBAN(iban);
      }),
    bankCode: Yup.string()
      .label(t("BIC code"))
      .test(
        "iban",
        t("Invalid BIC"),
        value => !value || ibantools.isValidBIC(value)
      ),
  });

  const initialValues = {
    bankName: organisation.bankName,
    bankAccount: organisation.bankAccount,
    bankCode: organisation.bankCode,
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={async values => {
        try {
          await modifyOrganisation({
            variables: {
              input: {
                organisationId: organisation.id,
                bankName: values.bankName,
                bankAccount: values.bankAccount,
                bankCode: values.bankCode,
              },
            },
          });
          onDone();
        } catch (error) {
          if (error instanceof Error)
            enqueueSnackbar(error.message, { variant: "error" });
        }
      }}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <Stack spacing={1} direction="column">
            <TextField
              name="bankName"
              label={t("Bank name")}
              disabled={false}
            />
            <TextField name="bankAccount" label={t("IBAN")} disabled={false} />
            <TextField name="bankCode" label={t("BIC code")} disabled={false} />
            <ManualSave
              onCancel={onDone}
              buttonLayout="inline"
              saveButtonTitle={t("Save", { ns: "Global" })}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
